import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { freeFetch, formatDate, formatDateShort, gripReps, GRIP_CHART_TITLES, GRIP_LABELS, GRIP_TILE_LABELS, RPE_SHORT_LABELS, SESSION_TYPE_TABLE_LABELS } from './api';
import StrengthChart from './StrengthChart';

const TABLE_TYPE_LABELS = SESSION_TYPE_TABLE_LABELS;

export default function FreeProgress() {
  const [data, setData] = useState(null);
  const [grip, setGrip] = useState('pullup');
  const navigate = useNavigate();

  useEffect(() => {
    freeFetch('/sessions')
      .then((d) => {
        setData(d);
        const logged = d.grips_logged || [];
        // Pull-up is the default tab unless the lifter has never logged one.
        if (logged.length && !logged.includes('pullup')) setGrip(logged[0]);
      })
      .catch(() => {});
  }, []);

  if (!data) {
    return <p className="text-mono-mute font-body" data-testid="free-progress-loading">Loading...</p>;
  }

  const gripsLogged = data.grips_logged || [];
  const prIds = new Set((data.pr_session_ids_by_grip || {})[grip] || []);
  const series = (data.strength_series_by_grip || {})[grip] || [];
  const highestByGrip = data.highest_load_by_grip || {};
  // A mixed-grip session appears under each grip it contains, showing only that grip's sets.
  const rows = data.sessions.filter((s) => gripReps(s, grip).length > 0);

  return (
    <div data-testid="free-progress-page">
      <h1 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight text-white leading-none mb-8">
        My Progress
      </h1>

      {data.sessions.length === 0 ? (
        <div className="border border-mono-line bg-mono-card p-10 text-center" data-testid="free-progress-empty">
          <p className="text-mono-mute font-body mb-6">No sessions logged yet.</p>
          <Link
            to="/free/log"
            className="inline-block bg-white hover:bg-[#E5E5E5] text-black font-heading font-bold uppercase tracking-wider rounded-none px-8 py-3 transition-colors"
            data-testid="free-progress-log-first-btn"
          >
            Log your first session
          </Link>
        </div>
      ) : (
        <>
          {gripsLogged.length > 1 && (
            <div className="flex gap-2 mb-6" data-testid="free-progress-grip-tabs">
              {gripsLogged.map((g) => (
                <button
                  key={g}
                  onClick={() => setGrip(g)}
                  className={`font-heading text-xs font-bold uppercase tracking-[0.15em] rounded-full px-4 py-2.5 border transition-colors ${
                    g === grip
                      ? 'bg-white border-white text-black'
                      : 'border-mono-line text-mono-mute hover:border-mono-mute hover:text-white'
                  }`}
                  data-testid={`free-progress-grip-tab-${g}`}
                >
                  {GRIP_LABELS[g]}
                </button>
              ))}
            </div>
          )}

          <StrengthChart series={series} title={GRIP_CHART_TITLES[grip]} />

          <div className="overflow-x-auto border border-mono-line">
            <table className="w-full text-left font-body text-sm" data-testid="free-progress-table">
              <thead>
                <tr className="border-b border-mono-line bg-mono-surface">
                  <th className="px-4 py-3 font-heading text-xs font-bold uppercase tracking-[0.15em] text-mono-mute whitespace-nowrap">
                    Date
                  </th>
                  <th className="px-4 py-3 font-heading text-xs font-bold uppercase tracking-[0.15em] text-mono-mute whitespace-nowrap">
                    Type
                  </th>
                  <th className="px-4 py-3 font-heading text-xs font-bold uppercase tracking-[0.15em] text-mono-mute whitespace-nowrap">
                    <span className="sm:hidden">Weight</span>
                    <span className="hidden sm:inline">Added Weight</span>
                  </th>
                  <th className="px-4 py-3 font-heading text-xs font-bold uppercase tracking-[0.15em] text-mono-mute whitespace-nowrap">
                    <span className="sm:hidden">Reps</span>
                    <span className="hidden sm:inline">Reps (S1/S2/S3)</span>
                  </th>
                  <th className="hidden sm:table-cell px-4 py-3 font-heading text-xs font-bold uppercase tracking-[0.15em] text-mono-mute whitespace-nowrap">
                    Effort
                  </th>
                  <th className="hidden sm:table-cell px-4 py-3 font-heading text-xs font-bold uppercase tracking-[0.15em] text-mono-mute whitespace-nowrap">
                    Feel
                  </th>
                  <th className="px-4 py-3" aria-hidden="true"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr
                    key={s.id}
                    onClick={() => navigate(`/free/session/${s.id}`)}
                    className="border-b border-mono-line/60 hover:bg-mono-card transition-colors cursor-pointer"
                    data-testid="free-progress-row"
                  >
                    <td className="px-4 py-3 text-white whitespace-nowrap">
                      <div className="flex flex-col justify-center gap-1 min-h-[2.5rem]">
                        <span>
                          <span className="sm:hidden">{formatDateShort(s.date)}</span>
                          <span className="hidden sm:inline">{formatDate(s.date)}</span>
                        </span>
                        {prIds.has(s.id) && (
                          <span
                            className="border border-crimson text-crimson font-heading text-[10px] font-bold uppercase tracking-[0.15em] leading-none px-1.5 py-1 w-fit"
                            data-testid="free-progress-pr-badge"
                          >
                            {grip === 'pullup' ? 'New PR' : `New PR · ${GRIP_LABELS[grip]}`}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-white whitespace-nowrap">{TABLE_TYPE_LABELS[s.session_type] || s.session_type}</td>
                    <td className="px-4 py-3 text-crimson font-semibold whitespace-nowrap">{s.load_kg}kg</td>
                    <td className="px-4 py-3 text-white whitespace-nowrap">
                      {gripReps(s, grip).join(' / ') || '—'}
                    </td>
                    <td className="hidden sm:table-cell px-4 py-3 text-white whitespace-nowrap">{RPE_SHORT_LABELS[s.rpe] || s.rpe}</td>
                    <td className="hidden sm:table-cell px-4 py-3 text-mono-mute whitespace-nowrap">{s.feel}</td>
                    <td className="px-4 py-3 text-right">
                      <ChevronRight className="h-4 w-4 text-mono-mute inline-block" data-testid="free-progress-row-chevron" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            <div className="border border-mono-line bg-mono-card p-6" data-testid="free-stat-highest-load">
              <p className="text-mono-mute font-heading text-xs font-bold uppercase tracking-[0.2em] mb-1">{GRIP_TILE_LABELS.pullup}</p>
              <p className="text-white font-heading text-3xl font-black">{highestByGrip.pullup ?? 0}kg</p>
            </div>
            {['chinup', 'neutral'].filter((g) => gripsLogged.includes(g)).map((g) => (
              <div key={g} className="border border-mono-line bg-mono-card p-6" data-testid={`free-stat-highest-${g}`}>
                <p className="text-mono-mute font-heading text-xs font-bold uppercase tracking-[0.2em] mb-1">{GRIP_TILE_LABELS[g]}</p>
                <p className="text-white font-heading text-3xl font-black">{highestByGrip[g] ?? 0}kg</p>
              </div>
            ))}
            <div className="border border-mono-line bg-mono-card p-6" data-testid="free-stat-sessions-week">
              <p className="text-mono-mute font-heading text-xs font-bold uppercase tracking-[0.2em] mb-1">Sessions this week</p>
              <p className="text-white font-heading text-3xl font-black">{data.sessions_this_week}</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
